import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Leaf } from "lucide-react";
import { IMAGES, scrollToId } from "@/data/content";

const LINES = ["Un luogo dove", "sentirsi a casa."];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="home" data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={IMAGES.heroBg}
          alt="Le colline verdi dell'Umbria attorno a Orto della Longevità"
          className="h-[120%] w-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/35 to-ink/25" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8 pb-20 sm:pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.3em] text-ivory/75"
          data-testid="hero-eyebrow"
        >
          <Leaf size={14} className="text-terracotta" />
          Orto della Longevità — Castel Giorgio · Terni
        </motion.p>

        <h1 className="mt-6 font-serif font-medium text-ivory text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight" data-testid="hero-title">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-ivory/85"
          data-testid="hero-subtitle"
        >
          Un luogo dove ogni persona viene accolta con attenzione, rispetto e professionalità.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-visit"
            onClick={() => scrollToId("contatti")}
            className="rounded-full bg-terracotta px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-wood hover:shadow-xl hover:shadow-terracotta/30 hover:-translate-y-0.5"
          >
            Prenota una visita
          </button>
          <button
            data-testid="hero-cta-info"
            onClick={() => scrollToId("contatti")}
            className="rounded-full border border-ivory/40 px-7 py-3.5 text-sm font-semibold text-ivory backdrop-blur-sm transition-all duration-300 hover:bg-ivory hover:text-ink"
          >
            Richiedi informazioni
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-ivory/20 pt-6"
          data-testid="hero-stats"
        >
          {[["46", "Posti letto"], ["3", "Residenze nel verde"], ["5", "Passi per essere accolti"]].map(([n, label]) => (
            <div key={label} className="flex items-baseline gap-2.5">
              <span className="font-serif text-3xl font-medium text-ivory">{n}</span>
              <span className="text-xs uppercase tracking-[0.18em] text-ivory/65">{label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 hidden sm:flex flex-col items-center gap-2 text-ivory/60"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
